import React, { useContext, useMemo } from 'react'
import { EduIf } from '@edulastic/common'
import { test } from '@edulastic/constants'
import useColorPickerPopOver from '../../../widgets/Passage/hooks/useColorPickerPopOver'
import useTextHighlight from '../../../widgets/Passage/hooks/useTextHighlight'
import { HighlightableContentContext } from './HighlightableContentProvider'
import { useSaveQuestionHighlights } from './hooks/useSaveItemHighlight'
import HighlightPopover from '../../../widgets/Passage/components/HighlightPopover'
import {
  ColorPickerContainer,
  Overlay,
} from '../../../widgets/Passage/components/styled-components'
import ColorPicker from '../../../widgets/Passage/components/ColorPicker'
import { StyledHighlightableStimulusWrapper } from './styled'
import { getIsHighlightToolEnabled } from '../../../themes/utils'

const HighlightableStimulus = ({
  children,
  question,
  stimulus,
  stackedTranslatorCurrentLanguage,
}) => {
  const {
    userWork,
    isStudentAttempt,
    disableResponse,
    setItemHighlights,
    utaPreferredLanguage,
    isLCBView,
    isExpressGrader,
    isStudentReport,
    studentLanguagePreference,
    currentLanguage,
    isHighlighterToolVisible,
    isTestPreview,
    playerSkinType,
    currentTestItem,
  } = useContext(HighlightableContentContext)

  const isHighlightToolEnabled = useMemo(
    () => getIsHighlightToolEnabled(currentTestItem || {}),
    [currentTestItem]
  )

  const { highlightedContent, saveHighlights } = useSaveQuestionHighlights({
    userWork,
    question,
    setItemHighlights,
    currentLanguage,
    isLCBView,
    isExpressGrader,
    isStudentReport,
    studentLanguagePreference,
    utaPreferredLanguage,
    isOption: false,
    stackedTranslatorCurrentLanguage,
  })

  const {
    closePopover,
    openPopover,
    isOpen,
    toggleOpen,
  } = useColorPickerPopOver()

  const {
    finishedRendering,
    handleClickBackdrop,
    updateColor,
    onSelectColor,
    handleMouseUp,
    mainContentsRef,
    content: updatedStimulusContent,
    selectHighlight,
  } = useTextHighlight({
    toggleOpen,
    passageContent: stimulus,
    highlightedContent,
    onChangeContent: saveHighlights,
    itemId: question?.id,
    disableResponse,
    isItemHighlight: true,
    playerSkinType,
  })

  const isHighlightAllowed =
    (isStudentAttempt || isTestPreview) &&
    isHighlightToolEnabled &&
    playerSkinType === test.playerSkinValues.quester

  return (
    <>
      <StyledHighlightableStimulusWrapper
        ref={mainContentsRef}
        id="highlightable-stimulus-wrapper"
        isHighlighterToolEnabled={isHighlighterToolVisible}
      >
        {children({
          updatedStimulusContent,
          finishedRendering,
        })}
      </StyledHighlightableStimulusWrapper>
      <EduIf condition={isHighlightAllowed}>
        <HighlightPopover
          getContainer={() => mainContentsRef.current}
          isOpen={isOpen && !selectHighlight && !disableResponse}
          onTextSelect={openPopover}
          onMouseUp={handleMouseUp}
          onTextUnselect={closePopover}
        >
          <ColorPickerContainer>
            <ColorPicker selectColor={onSelectColor} />
          </ColorPickerContainer>
        </HighlightPopover>
      </EduIf>
      <EduIf condition={selectHighlight && !disableResponse}>
        {() => (
          <>
            <ColorPickerContainer
              style={{ ...selectHighlight, position: 'absolute' }}
            >
              <ColorPicker selectColor={updateColor} bg={selectHighlight.bg} />
            </ColorPickerContainer>
            <Overlay onClick={handleClickBackdrop} />
          </>
        )}
      </EduIf>
    </>
  )
}

export default HighlightableStimulus
